import { KatDropdown, KatInput } from '@amzn/katal-react';
import KatInputWithSupport from 'src/components/general/KatInputWithSupport/KatInputWithSupport';

// Union type for input event handling to maintain type safety
type InputEventType = KatInput.ChangeEvent | { currentTarget: { value: string } };
import {
  CMSMetadata,
  Slot,
  SegmentKey,
  TargetSegment,
  SelectionType,
  PageId,
  TabletCategory,
  ContentType,
  DeviceFamily,
  ConfigurationId,
} from '@amzn/fire-tv-campaign-automation-service';

import { SELECTION_TYPES } from 'src/components/general/MultiSelectDropDown/constants';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { useCampaignMetadata } from 'src/hooks/useCampaignMetadata';
import styles from './CampaignMetadataInputSection.module.css';
import {
  getSlotOptionsForPageId,
  getPageIdsForPageType,
  getMetadataConfig,
  getPageTypeOptions,
  getBusinessGroupOptions,
} from 'src/configs/campaignMetadataConfig';
import { getTargetSegmentConfiguration } from 'src/configs/targetSegmentConfiguration';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelection } from 'src/contexts/SelectionContext';
import { useEmbedded } from 'src/contexts/EmbeddedContext';
import { ProvisionedConstants, STAGE, TimeZoneConstants, StateConstants } from 'src/constants';
import { useDebouncedCallback } from 'src/hooks/useDebounce';
import { getEndDate, getStartDate } from 'src/utils/DateTimeUtils';
import { getPlacementFeatures } from 'src/configs/placementFeatureConfig';
import { PropUtils } from 'src/utils/PropUtils';
import { deriveContentTypeFromCategory } from 'src/utils/CategoryUtils';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { logger } from 'src/logger';
import { getConfigs } from 'src/utils/clients/controlPlane/apiMethods';

// Define regex outside component to avoid recreation
const SEGMENT_ID_REGEX = /\(([^)]+)\)$/;
import { DateTime } from 'luxon';
import { DateTimeRangePicker } from 'src/components/general/DateTimeRangePicker';
import { MultiSelectDropDown } from 'src/components/general/MultiSelectDropDown';
import type {
  SelectedItem,
  MultiSelectDropDownOption,
} from 'src/components/general/MultiSelectDropDown/types';
import { WeblabInput } from 'src/components/pages/campaignAutomation/input/WeblabInput';

export const CampaignMetadataInputSection = () => {
  const { placement, marketplace, deviceFamily } = useSelection();
  const [isLoading, setIsLoading] = useState(true);
  const { metadata, updateMetadata } = useCampaignMetadata();
  const [selectedTargetingItems, setSelectedTargetingItems] = useState<SelectedItem[]>([]);
  const [dynamicDeviceTypes, setDynamicDeviceTypes] = useState<{ name: string; value: string }[]>(
    [],
  );
  const controlPlaneClient = getControlPlaneClient();
  const { user } = useAuthentication();
  const userAlias = getUserAlias(user);

  /**
   * State to store dropdown options for metadata fields.
   * Maps metadata keys to arrays of name-value option pairs.
   */
  const [optionsMap, updateOptionsMap] = useState<
    Partial<
      Record<
        keyof CMSMetadata,
        {
          name: string;
          value: string;
        }[]
      >
    >
  >({
    pageType: getPageTypeOptions(deviceFamily, placement),
    businessGroupId: getBusinessGroupOptions(deviceFamily, placement),
    slotName: getSlotOptionsForPageId(metadata.pageId),
  });

  /**
   * The timezone to use for date/time handling, based on marketplace selection.
   * Defaults to UTC until a marketplace is selected.
   */
  const [enforcedTimeZone, setEnforcedTimeZone] =
    useState<TimeZoneConstants.AllowedTimezone>('UTC');

  /**
   * Recommended date-time values to initialize the DateTimeRangePicker with.
   * These are calculated based on the selected marketplace's timezone and time preferences.
   */
  const [recommendedDates, setRecommendedDates] = useState<{
    start: DateTime | undefined;
    end: DateTime | undefined;
  }>();

  /**
   * Updates slot options when page ID changes and selects the first slot by default.
   * @dependency metadata.pageId - Re-runs when the page ID changes
   */
  useEffect(() => {
    const slots = getSlotOptionsForPageId(metadata.pageId);
    updateOptionsMap((prev) => ({
      ...prev,
      slotName: slots,
    }));

    // Set slotName to first option in the list if available
    if (slots && slots.length > 0) {
      updateMetadata({ slotName: slots[0].value as Slot });
    }
  }, [metadata.pageId]);

  /**
   * Updates Page ID options when Page Type changes.
   * Enforces Page Type → Page ID relationship per Burton Quip.
   */
  useEffect(() => {
    const pageIds = getPageIdsForPageType(metadata.pageType);
    updateOptionsMap((prev) => ({
      ...prev,
      pageId: pageIds,
    }));

    // Reset pageId if current selection is invalid for new page type
    if (pageIds.length > 0 && !pageIds.find((p) => p.value === metadata.pageId)) {
      updateMetadata({ pageId: pageIds[0].value as PageId });
    }
  }, [metadata.pageType]);

  /**
   * Sets up the recommended date range and timezone based on the selected marketplace.
   * These values are used to initialize the DateTimeRangePicker with appropriate defaults.
   *
   * @dependency marketplace - Re-runs when the marketplace selection changes
   */
  useEffect(() => {
    if (!marketplace) return;
    setIsLoading(true);

    // Get timezone and time configuration for the selected marketplace
    const tzConfig = TimeZoneConstants.MARKETPLACE_TO_TZ_MAPPING[marketplace];
    if (!tzConfig) return;

    const { startTime, endTime, timeZone } = tzConfig;

    // Calculate appropriate start and end dates based on marketplace configuration
    const start = getStartDate(timeZone, startTime);
    const end = getEndDate(start, endTime);

    // Update the timezone display for the date picker
    setEnforcedTimeZone(timeZone);

    // Only update recommendedDates when they actually change to prevent unnecessary renders
    setRecommendedDates((prev) => {
      if (!prev || !prev.start || !prev.end || !start.equals(prev.start) || !end.equals(prev.end)) {
        return { start, end };
      }
      return prev;
    });

    setIsLoading(false);
  }, [marketplace]);

  /**
   * Fetch dynamic device types from GetConfigs API
   */
  useEffect(() => {
    if (!marketplace || !deviceFamily || !controlPlaneClient) {
      return;
    }

    if (deviceFamily !== DeviceFamily.FIRE_TV) {
      setDynamicDeviceTypes([]);
      return;
    }

    const loadDeviceTypes = async () => {
      try {
        const response = await getConfigs(controlPlaneClient, {
          configurations: [ConfigurationId.DEVICE_TYPE_ID],
          requestContext: {
            marketPlace: marketplace,
            deviceFamily,
            actor: userAlias,
          },
        });

        const deviceTypeResult = response.results?.[ConfigurationId.DEVICE_TYPE_ID];

        if (deviceTypeResult?.errorDetail) {
          logger.error('Failed to fetch device types', {
            error: deviceTypeResult.errorDetail,
          });
          setDynamicDeviceTypes([]);
          return;
        }

        const deviceTypes = deviceTypeResult?.config?.deviceTypes || [];
        setDynamicDeviceTypes(
          deviceTypes.map((dt: { displayName: string; value: string }) => ({
            name: dt.displayName,
            value: dt.value,
          })),
        );
      } catch (error) {
        logger.error('Failed to load device types', { error: error as Error });
        setDynamicDeviceTypes([]);
      }
    };

    loadDeviceTypes();
  }, [marketplace, deviceFamily, controlPlaneClient, userAlias]);

  /**
   * Initialize selectedTargetingItems from existing metadata on component mount
   */
  useEffect(() => {
    if (metadata.targetSegments?.length) {
      const targetSegmentConfig = getTargetSegmentConfiguration(
        ProvisionedConstants.STAGE as STAGE,
        marketplace,
      );
      const selectedItems: SelectedItem[] = metadata.targetSegments.map((segment) => {
        // Find the display name from targetingConfigs
        const configs = targetSegmentConfig?.[segment.segmentKey];
        const config = configs?.find((c) => c.id === segment.segmentId);
        const displayValue = config ? `${config.name} (${config.id})` : segment.segmentId;

        return {
          value: displayValue,
          label: displayValue,
          type:
            segment.selectionType === SelectionType.INCLUDE
              ? SELECTION_TYPES.INCLUDE
              : SELECTION_TYPES.EXCLUDE,
          group: segment.segmentKey,
        };
      });
      setSelectedTargetingItems(selectedItems);
    }
  }, [metadata.targetSegments, marketplace]);
  /**
   * Transform targetingConfigs to the format expected by MultiSelectDropDown
   */
  const transformedTargetingConfigs = useMemo(() => {
    const targetSegmentConfig = getTargetSegmentConfiguration(
      ProvisionedConstants.STAGE as STAGE,
      marketplace,
    );
    const transformed = Object.entries(targetSegmentConfig).reduce(
      (acc, [key, configs]) => {
        if (configs) {
          acc[key] = configs.map((config) => ({
            id: config.id,
            value: config.name,
          }));
        }
        return acc;
      },
      {} as Record<string, MultiSelectDropDownOption[]>,
    );

    // Merge dynamic device types from API
    if (dynamicDeviceTypes.length > 0) {
      transformed[SegmentKey.DEVICE_TYPE_ID] = dynamicDeviceTypes.map((dt) => ({
        id: dt.value,
        value: dt.name,
      }));
    }

    return transformed;
  }, [marketplace, ProvisionedConstants.STAGE, dynamicDeviceTypes]);

  /**
   * Updates segments by type, preserving other segment types.
   */
  const updateSegmentsByType = useCallback(
    (segmentKeys: SegmentKey[], newSegments: TargetSegment[]) => {
      const current = metadata.targetSegments || [];
      const others = current.filter((s) => !segmentKeys.includes(s.segmentKey));
      updateMetadata({ targetSegments: [...others, ...newSegments] });
    },
    [metadata.targetSegments, updateMetadata],
  );

  /**
   * Handles targeting selection changes from the MultiSelectDropDown component.
   */
  const handleTargetingSelectionChange = useCallback(
    (selectedItems: SelectedItem[]) => {
      setSelectedTargetingItems(selectedItems);

      // Convert SelectedItems to TargetSegments
      const newTargetSegments = selectedItems
        .filter((item) => Object.values(SegmentKey).includes(item.group as SegmentKey))
        .map((item) => ({
          segmentId: item.value,
          segmentKey: item.group as SegmentKey,
          selectionType:
            item.type === SELECTION_TYPES.INCLUDE ? SelectionType.INCLUDE : SelectionType.EXCLUDE,
        }));

      updateSegmentsByType(
        [SegmentKey.BULLSEYE_SEGMENT, SegmentKey.DEVICE_TYPE_ID],
        newTargetSegments,
      );
    },
    [updateSegmentsByType],
  );

  // Note: KatInput uses custom event types (KatCustomEvent), not standard React events
  const handleInputChange = (key: keyof CMSMetadata, event: InputEventType) => {
    updateMetadata({ [key]: event.currentTarget.value });
  };

  const handleDebouncedInputChange = useDebouncedCallback(
    (key: keyof CMSMetadata, value: string) => {
      const trimmedValue = value.trim();
      if (trimmedValue || value === '') {
        updateMetadata({ [key]: trimmedValue });
      }
    },
    StateConstants.INPUT_DEBOUNCE_MS,
  );

  const handleDropdownChange = (key: keyof CMSMetadata, event: KatDropdown.ChangeEvent) => {
    updateMetadata({ [key]: event.detail.value });
  };

  /**
   * Handles date range changes from the DateTimeRangePicker component.
   * Efficiently updates campaign start and end dates in the metadata context
   * while avoiding unnecessary updates when values haven't actually changed.
   *
   * @param start - The selected start date/time or null if cleared
   * @param end - The selected end date/time or null if cleared
   */
  const handleDateRangeChange = useCallback(
    (start: DateTime | null, end: DateTime | null) => {
      // Compare with existing values to avoid unnecessary updates
      const currentStartDate = metadata.campaignStartDate as number | undefined;
      const currentEndDate = metadata.campaignEndDate as number | undefined;

      // Convert DateTime to epoch seconds
      const startEpoch = start ? Math.floor(start.toJSDate().getTime() / 1000) : undefined;
      const endEpoch = end ? Math.floor(end.toJSDate().getTime() / 1000) : undefined;

      // Determine if start date has actually changed
      const startChanged =
        (!currentStartDate && startEpoch) ||
        (currentStartDate && !startEpoch) ||
        (currentStartDate && startEpoch && currentStartDate !== startEpoch);

      // Determine if end date has actually changed
      const endChanged =
        (!currentEndDate && endEpoch) ||
        (currentEndDate && !endEpoch) ||
        (currentEndDate && endEpoch && currentEndDate !== endEpoch);

      // Only update metadata if at least one value has actually changed
      if (startChanged || endChanged) {
        updateMetadata({
          campaignStartDate: startEpoch,
          campaignEndDate: endEpoch,
        });
      }
    },
    [updateMetadata, metadata.campaignStartDate, metadata.campaignEndDate],
  );

  /**
   * Handles weblab target segments changes.
   */
  const handleWeblabTargetSegmentsChange = useCallback(
    (weblabSegments: TargetSegment[]) => {
      updateSegmentsByType([SegmentKey.WEBLAB_SERVICE], weblabSegments);
    },
    [updateSegmentsByType],
  );

  /**
   * Auto-derive contentType when category changes (but allow manual override)
   */
  const prevCategoryRef = useRef<TabletCategory | undefined>(metadata.category);

  // Get complete metadata config including device-family-specific fields
  const CAMPAIGN_METADATA_CONFIG = useMemo(() => getMetadataConfig(deviceFamily), [deviceFamily]);

  useEffect(() => {
    // Only auto-derive if category actually changed (not on every render)
    if (metadata.category && metadata.category !== prevCategoryRef.current) {
      const derivedContentType = deriveContentTypeFromCategory(metadata.category);
      updateMetadata({ contentType: derivedContentType });
      prevCategoryRef.current = metadata.category;
    }
  }, [metadata.category, updateMetadata]);

  /**
   * Handles category change and derives content type
   */
  const { isEmbedded } = useEmbedded();
  const placementFeatures = getPlacementFeatures(placement);

  if (!placementFeatures.showCampaignMetadata || isEmbedded) {
    return null;
  }

  const renderMetadataItem = (metadataItem: keyof CMSMetadata) => {
    const config = CAMPAIGN_METADATA_CONFIG[metadataItem];
    if (!config) return null;

    const key = metadataItem as keyof CMSMetadata;

    switch (config.type) {
      case 'text':
        const handler =
          config.handlerType === 'onInput'
            ? {
                onInput: (e: InputEventType) =>
                  handleDebouncedInputChange(key, e.currentTarget.value),
              }
            : { onChange: (e: InputEventType) => handleInputChange(key, e) };

        return (
          <KatInput
            label={config.label}
            value={metadata[key]?.toString()}
            {...handler}
            placeholder={config.placeholder}
            data-testid={config.dataTestId}
            required={config.required}
            min={config.minLength?.toString()}
            maxlength={config.maxLength?.toString()}
          />
        );
      case 'text-with-support':
        return (
          <KatInputWithSupport
            label={config.label}
            value={metadata[key]?.toString()}
            onChange={(e) => handleInputChange(key, e)}
            onInput={(e) => handleInputChange(key, e)}
            placeholder={config.placeholder}
            data-testid={config.dataTestId}
            required={config.required}
            min={config.minLength?.toString()}
            maxlength={config.maxLength?.toString()}
            tooltipConfig={config.tooltipConfig}
            tooltipWidth={config.tooltipWidth}
          />
        );
      case 'date-time':
        // Special handling for campaign date selection using the DateTimeRangePicker
        // Only render for campaignStartDate (to avoid duplicate UI) and when not loading
        if (metadataItem === 'campaignStartDate' && !isLoading) {
          return (
            <div className={styles.datePickerGroup}>
              <div className={styles.datePickerHeader}>
                <span className={styles.dateTimeLabel}>Select Campaign Period</span>
                <span className={styles.timezoneLabel}>[Timezone: {enforcedTimeZone}]</span>
              </div>
              <div className={styles.datePickerControl}>
                {/* 
                  DateTimeRangePicker handles both start and end date selection in a single component.
                  It manages both campaignStartDate and campaignEndDate metadata fields.
                */}
                <DateTimeRangePicker
                  timezone={enforcedTimeZone}
                  onRangeChange={handleDateRangeChange}
                  recommendedStart={recommendedDates?.start}
                  recommendedEnd={recommendedDates?.end}
                  pickerIds={{ start: 'campaign-start-date-input', end: 'campaign-end-date-input' }}
                  data-testid="campaign-date-picker"
                />
              </div>
            </div>
          );
        }
        return null;
      case 'drop-down':
        return (
          <StyledDropdown
            label={config.label}
            value={metadata[key]?.toString()}
            onChange={(e) => handleDropdownChange(metadataItem, e)}
            options={optionsMap[metadataItem]?.length ? optionsMap[metadataItem] : config.options} // Uses dynamic options from optionsMap if available, otherwise falls back to static config options
            placeholder={config.placeholder}
            data-testid={config.dataTestId}
          />
        );
      default:
        return null;
    }
  };

  // Group metadata items by their rowGroup (including device-family-specific fields)
  const groupedMetadata = Object.entries(CAMPAIGN_METADATA_CONFIG).reduce<
    Record<string, Array<keyof CMSMetadata>>
  >((acc, [key, config]) => {
    const group = config.rowGroup || 'default';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(key as keyof CMSMetadata);
    return acc;
  }, {});

  return (
    <div className={styles.container} data-testid="campaign-metadata-section">
      {Object.entries(groupedMetadata).map(([group, items]) => (
        <div key={group} className={styles.fieldGroup}>
          {items
            .filter((item) => item !== 'campaignEndDate')
            .map((item) => {
              const config = CAMPAIGN_METADATA_CONFIG[item];
              if (!config) return null;

              return (
                <div key={item} className={styles.inputGroup}>
                  {renderMetadataItem(item)}
                </div>
              );
            })}
        </div>
      ))}

      {/* Targeting Configuration Section */}
      <div className={styles.row}>
        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
          <MultiSelectDropDown
            label="Targeting Segment"
            options={transformedTargetingConfigs}
            placeholder="Search targeting configurations..."
            onSelectionChange={handleTargetingSelectionChange}
          />
        </div>
      </div>

      {/* Weblab Configuration Section */}
      <div className={styles.row}>
        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
          <WeblabInput
            weblabSegments={
              metadata.targetSegments?.filter((s) => s.segmentKey === SegmentKey.WEBLAB_SERVICE) ||
              []
            }
            onWeblabSegmentsChange={handleWeblabTargetSegmentsChange}
          />
        </div>
      </div>
    </div>
  );
};
